import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    contentWidth: Number
  }
  static targets = ["withDrawer", "zoomableContainer"];

  connect() {
    this.drawerWidths = {
      leftDrawer: 0,
      rightDrawer: 0,
    };

    this.handleDrawerWidthChange = this.handleDrawerWidthChange.bind(this);
    this.adjustContent = this.adjustContent.bind(this);
    this.adjustAfterResize = this.adjustAfterResize.bind(this);
    this.resetLayout = this.resetLayout.bind(this);
    this.initializeLayout = this.initializeLayout.bind(this);

    this.element.addEventListener("drawer:widthChanged", this.handleDrawerWidthChange);
    window.addEventListener("resize", this.adjustContent);
    this.element.addEventListener("transitionend", this.adjustAfterResize);

    document.addEventListener("turbo:before-frame-render", this.resetLayout);
    document.addEventListener("turbo:frame-render", this.initializeLayout);

    this._adjusting = false;
    this._pendingAdjust = null;

    // MutationObserver detects when child content is added/changed
    // (e.g. Vue grid components rendering, AI text loading)
    this.mutationObserver = new MutationObserver(() => {
      if (!this._adjusting) {
        if (this._pendingAdjust) cancelAnimationFrame(this._pendingAdjust);
        this._pendingAdjust = requestAnimationFrame(this.adjustContent);
      }
    });

    requestAnimationFrame(this.adjustContent);
  }

  disconnect() {
    this.element.removeEventListener("drawer:widthChanged", this.handleDrawerWidthChange);
    window.removeEventListener("resize", this.adjustContent);
    this.element.removeEventListener("transitionend", this.adjustAfterResize);
    document.removeEventListener("turbo:before-frame-render", this.resetLayout);
    document.removeEventListener("turbo:frame-render", this.initializeLayout);
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  resetLayout(event) {
    if (window.multiselectClickHandler) {
      window.removeEventListener("click", window.multiselectClickHandler);
    }
  }

  initializeLayout(event) {
    requestAnimationFrame(() => {
      this.adjustContent();
      setTimeout(() => this.adjustContent(), 100);
    });
  }

  // The container height is measured, so an accordion that is still opening reads too short.
  adjustAfterResize(event) {
    if (/height|grid-template-rows/.test(event.propertyName)) this.adjustContent();
  }

  handleDrawerWidthChange(event) {
    const { drawerId, width } = event.detail;

    if (drawerId in this.drawerWidths) {
      this.drawerWidths[drawerId] = width;
      this.adjustContent();
    } else {
      console.warn(`Unknown drawerId: ${drawerId}`);
    }
  }

  adjustContent() {
    const viewportWidth = window.innerWidth;
    const remainingWidth = viewportWidth - (this.drawerWidths.leftDrawer + 40) - (this.drawerWidths.rightDrawer + 40);

    if (this.hasWithDrawerTarget) {
      this.withDrawerTarget.style.paddingLeft = `${this.drawerWidths.leftDrawer + 40}px`;
      this.withDrawerTarget.style.paddingRight = `${this.drawerWidths.rightDrawer + 40}px`;
    }

    if (this.hasZoomableContainerTarget) {
      this._adjusting = true;
      const originalWidth = this.contentWidthValue || 1110;

      const scale = Math.min(1, remainingWidth / originalWidth);

      this.zoomableContainerTarget.style.width = `${originalWidth}px`;
      this.zoomableContainerTarget.style.margin = "auto";
      this.zoomableContainerTarget.style.padding = "0";

      // A fractional transform scale paints rounded borders with square corner artefacts.
      if (CSS.supports("zoom", "1")) {
        this.zoomableContainerTarget.style.height = "auto";
        this.zoomableContainerTarget.style.zoom = `${scale}`;
      } else {
        // Temporarily remove explicit height to get true scrollHeight
        this.zoomableContainerTarget.style.height = "auto";
        const transformedHeight = this.zoomableContainerTarget.scrollHeight * scale;

        this.zoomableContainerTarget.style.height = `${transformedHeight}px`;
        this.zoomableContainerTarget.style.transform = `scale(${scale})`;
        this.zoomableContainerTarget.style.transformOrigin = "left top";
      }

      if (this.mutationObserver) {
        this.mutationObserver.observe(this.zoomableContainerTarget, {
          childList: true,
          subtree: true,
          attributes: true
        });
      }
      requestAnimationFrame(() => { this._adjusting = false; });
    }
  }
}
